import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { Markup, CreateMarkup, UpdateMarkup, BulkUpdateMarkup } from '@estimator/shared';

export function useMarkups(projectId: string) {
  return useQuery({
    queryKey: ['markups', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ markups: Markup[] } | Markup[]>(
        `/projects/${projectId}/markups`,
      );
      // Server returns { grouped, markups } — extract the array
      if (Array.isArray(data)) return data;
      return (data as { markups: Markup[] }).markups ?? [];
    },
    enabled: !!projectId,
  });
}

export function useUpdateMarkup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      ...body
    }: UpdateMarkup & { id: string; projectId: string }) => {
      const { data } = await apiClient.patch<Markup>(
        `/markups/${id}`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['markups', vars.projectId] });
    },
  });
}

export function useCreateMarkup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      ...body
    }: Partial<CreateMarkup> & { projectId: string; [key: string]: unknown }) => {
      const { data } = await apiClient.post<Markup>(
        `/projects/${projectId}/markups`,
        body,
      );
      return data;
    },
    onMutate: async (vars) => {
      // Cancel in-flight refetches so they don't overwrite our optimistic entry
      await queryClient.cancelQueries({ queryKey: ['markups', vars.projectId] });

      const previous = queryClient.getQueryData<Markup[]>(['markups', vars.projectId]);

      // Build a temporary markup that the canvas can render immediately
      const { projectId: _pid, ...body } = vars;
      const optimistic: Markup = {
        id: `temp-${Date.now()}`,
        project_id: vars.projectId,
        knowledge_item_id: (body.knowledge_item_id as string) ?? '',
        page_id: (body.page_id as string) ?? null,
        geometry: body.geometry as any,
        label: (body.label as string) ?? '',
        category: (body.category as string) ?? 'AREA',
        measurement_type: (body.measurement_type as string) ?? null,
        measured_value: (body.measured_value as number) ?? null,
        measured_unit: (body.measured_unit as string) ?? null,
        color: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Markup;

      queryClient.setQueryData<Markup[]>(
        ['markups', vars.projectId],
        (old) => [...(old ?? []), optimistic],
      );

      return { previous };
    },
    onError: (_err, vars, context) => {
      // Roll back to the previous cache state
      if (context?.previous) {
        queryClient.setQueryData(['markups', vars.projectId], context.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      // Refetch to reconcile with the real server state
      queryClient.invalidateQueries({ queryKey: ['markups', vars.projectId] });
    },
  });
}

export function useDeleteMarkup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      await apiClient.delete(`/markups/${id}`);
      return { id, projectId };
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['markups', vars.projectId] });
    },
  });
}

export function useBulkUpdateMarkups(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: BulkUpdateMarkup) => {
      const { data } = await apiClient.patch<Markup[]>(
        `/projects/${projectId}/markups/bulk`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['markups', projectId] });
    },
  });
}
